'use client'

import React from 'react'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { useBirhaus } from '@birhaus/provider'
import { Eye, EyeOff, CreditCard, Wallet, PiggyBank, TrendingUp, AlertCircle } from 'lucide-react'
import { formatCurrency } from '../utils/currency'
import { BirhausCurrencyDisplay } from './BirhausCurrencyDisplay'
import type { BirhausAccountSummaryProps, Account, AccountType, AccountStatus } from '../types/financial'

// Local cn utility
const cn = (...inputs: any[]) => twMerge(clsx(inputs))

// Account type icons and colors
const accountTypeConfig: Record<AccountType, { 
  icon: React.ReactNode
  colorClass: string
  nameEs: string
  nameEn: string
}> = {
  corriente: {
    icon: <CreditCard size={20} />,
    colorClass: 'text-blue-600 bg-blue-100',
    nameEs: 'Cuenta Corriente',
    nameEn: 'Checking Account'
  },
  ahorros: {
    icon: <PiggyBank size={20} />,
    colorClass: 'text-green-600 bg-green-100',
    nameEs: 'Cuenta de Ahorros',
    nameEn: 'Savings Account'
  },
  credito: {
    icon: <CreditCard size={20} />,
    colorClass: 'text-purple-600 bg-purple-100',
    nameEs: 'Línea de Crédito',
    nameEn: 'Credit Line'
  },
  inversion: {
    icon: <TrendingUp size={20} />,
    colorClass: 'text-orange-600 bg-orange-100',
    nameEs: 'Cuenta de Inversión',
    nameEn: 'Investment Account'
  }
}

// Account status configuration
const accountStatusConfig: Record<AccountStatus, {
  colorClass: string
  nameEs: string
  nameEn: string
}> = {
  activa: {
    colorClass: 'text-green-700 bg-green-100',
    nameEs: 'Activa',
    nameEn: 'Active'
  },
  inactiva: {
    colorClass: 'text-gray-700 bg-gray-100',
    nameEs: 'Inactiva',
    nameEn: 'Inactive'
  },
  bloqueada: {
    colorClass: 'text-red-700 bg-red-100',
    nameEs: 'Bloqueada',
    nameEn: 'Blocked'
  },
  cerrada: {
    colorClass: 'text-gray-700 bg-gray-200',
    nameEs: 'Cerrada',
    nameEn: 'Closed'
  }
}

/**
 * BirhausAccountSummary - Account summary display component
 * 
 * Features:
 * - Multiple account types with appropriate icons and colors
 * - Balance visibility toggle for privacy
 * - Account status indicators
 * - Spanish-first account type and status names
 * - Accessibility compliant with proper ARIA labels
 * - Responsive layout for different screen sizes
 * - Click handlers for account selection
 * - Support for inactive/closed account filtering
 */
export function BirhausAccountSummary({
  accounts,
  showBalances = true,
  showInactive = false,
  onAccountClick,
  className,
  ...props
}: BirhausAccountSummaryProps) {
  const { language, reportViolation } = useBirhaus()
  const [balancesVisible, setBalancesVisible] = React.useState(showBalances)

  // Filter accounts based on showInactive setting
  const filteredAccounts = React.useMemo(() => {
    return accounts.filter(account => 
      showInactive || (account.estado === 'activa')
    )
  }, [accounts, showInactive])

  // BIRHAUS validation: Check cognitive load (max 7 accounts visible - Miller's Law)
  React.useEffect(() => {
    if (filteredAccounts.length > 7) {
      reportViolation({
        type: 'cognitive',
        severity: 'warning',
        element: document.createElement('div'),
        message: `Account summary shows ${filteredAccounts.length} accounts (max recommended: 7)`,
        messageEs: `Resumen de cuentas muestra ${filteredAccounts.length} cuentas (máximo recomendado: 7)`,
        messageEn: `Account summary shows ${filteredAccounts.length} accounts (max recommended: 7)`,
        recommendation: 'Consider pagination or grouping for large numbers of accounts',
        birhausPrinciple: 1 // Cognitive load reduction
      })
    }
  }, [filteredAccounts.length, reportViolation])

  // Calculate total balance across all active accounts
  const totalBalance = React.useMemo(() => {
    return filteredAccounts
      .filter(account => account.estado === 'activa')
      .reduce((sum, account) => {
        // Convert all to PYG for summary (in real app, use proper currency conversion)
        const balance = account.moneda === 'PYG' ? account.saldo : account.saldo * 7300
        return sum + balance
      }, 0)
  }, [filteredAccounts])

  // Toggle balance visibility
  const toggleBalanceVisibility = React.useCallback(() => {
    setBalancesVisible(prev => !prev)
  }, [])

  // Handle account click
  const handleAccountClick = React.useCallback((account: Account) => {
    onAccountClick?.(account)
  }, [onAccountClick])

  if (filteredAccounts.length === 0) {
    return (
      <div className={cn('p-6 text-center text-birhaus-muted-foreground', className)}>
        <Wallet size={48} className="mx-auto mb-4 text-birhaus-muted-foreground/50" />
        <p>
          {language === 'es' 
            ? 'No hay cuentas disponibles' 
            : 'No accounts available'}
        </p>
      </div>
    )
  }

  return (
    <div className={cn('space-y-4', className)} {...props}>
      {/* Summary header with total balance */}
      <div className="bg-gradient-to-r from-birhaus-primary to-birhaus-primary/80 p-6 rounded-lg text-white">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold">
            {language === 'es' ? 'Resumen de Cuentas' : 'Account Summary'}
          </h2>
          
          <button
            onClick={toggleBalanceVisibility}
            className="p-2 hover:bg-white/20 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white/50"
            aria-label={balancesVisible 
              ? (language === 'es' ? 'Ocultar saldos' : 'Hide balances')
              : (language === 'es' ? 'Mostrar saldos' : 'Show balances')}
          >
            {balancesVisible ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
        
        <div className="space-y-1">
          <p className="text-white/80 text-sm">
            {language === 'es' ? 'Balance total' : 'Total balance'}
          </p>
          <p className="text-2xl font-bold">
            {balancesVisible ? (
              <BirhausCurrencyDisplay 
                amount={totalBalance} 
                currency="PYG" 
                className="text-white"
              />
            ) : (
              '••••••••'
            )}
          </p>
          <p className="text-white/70 text-xs">
            {filteredAccounts.filter(a => a.estado === 'activa').length}{' '}
            {language === 'es' ? 'cuentas activas' : 'active accounts'}
          </p>
        </div>
      </div>

      {/* Account list */}
      <div className="space-y-3">
        {filteredAccounts.map((account) => {
          const typeConfig = accountTypeConfig[account.tipo]
          const statusConfig = accountStatusConfig[account.estado]
          const displayName = language === 'es' ? account.nombreEs : (account.nombreEn || account.nombreEs)
          const typeName = language === 'es' ? typeConfig.nameEs : typeConfig.nameEn
          const statusName = language === 'es' ? statusConfig.nameEs : statusConfig.nameEn
          
          const isClickable = Boolean(onAccountClick)
          const isInactive = account.estado !== 'activa'

          return (
            <div
              key={account.id}
              className={cn(
                'p-4 border border-birhaus-border rounded-lg transition-all duration-200',
                isClickable && 'cursor-pointer hover:border-birhaus-primary/50 hover:shadow-sm',
                isInactive && 'opacity-60',
                'focus:outline-none focus:ring-2 focus:ring-birhaus-primary focus:border-birhaus-primary'
              )}
              onClick={() => isClickable && handleAccountClick(account)}
              onKeyDown={(e) => {
                if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault()
                  handleAccountClick(account)
                }
              }}
              tabIndex={isClickable ? 0 : -1}
              role={isClickable ? 'button' : undefined}
              aria-label={`${displayName}, ${typeName}, ${balancesVisible ? formatCurrency(account.saldo, { currency: account.moneda }) : 'saldo oculto'}`}
            >
              <div className="flex items-center gap-4">
                {/* Account type icon */}
                <div className={cn(
                  'p-2 rounded-full flex-shrink-0',
                  typeConfig.colorClass
                )}>
                  {typeConfig.icon}
                </div>

                {/* Account info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-medium text-birhaus-foreground truncate">
                      {displayName}
                    </h3>
                    
                    {/* Account status badge */}
                    {isInactive && (
                      <span className={cn(
                        'px-2 py-1 text-xs font-medium rounded-full',
                        statusConfig.colorClass
                      )}>
                        {statusName}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-birhaus-muted-foreground">
                        {typeName} • {account.numero}
                      </p>
                      
                      {/* Account limits info */}
                      {(account.limiteDiario || account.limiteMensual) && (
                        <p className="text-xs text-birhaus-muted-foreground">
                          {account.limiteDiario && (
                            <span>
                              {language === 'es' ? 'Límite diario: ' : 'Daily limit: '}
                              {formatCurrency(account.limiteDiario, { currency: account.moneda })}
                            </span>
                          )}
                          {account.limiteDiario && account.limiteMensual && ' • '}
                          {account.limiteMensual && (
                            <span>
                              {language === 'es' ? 'Límite mensual: ' : 'Monthly limit: '}
                              {formatCurrency(account.limiteMensual, { currency: account.moneda })}
                            </span>
                          )}
                        </p>
                      )}
                    </div>

                    {/* Balance display */}
                    <div className="text-right">
                      <p className={cn(
                        'font-semibold',
                        account.saldo >= 0 ? 'text-green-600' : 'text-red-600'
                      )}>
                        {balancesVisible ? (
                          <BirhausCurrencyDisplay 
                            amount={account.saldo} 
                            currency={account.moneda}
                          />
                        ) : (
                          '••••••'
                        )}
                      </p>
                      
                      <p className="text-xs text-birhaus-muted-foreground">
                        {language === 'es' ? 'Última actividad: ' : 'Last activity: '}
                        {account.ultimaActividad.toLocaleDateString(language === 'es' ? 'es-PY' : 'en-US')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Warning indicator for blocked accounts */}
                {account.estado === 'bloqueada' && (
                  <div className="flex-shrink-0">
                    <AlertCircle size={20} className="text-red-600" />
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Footer info */}
      {accounts.length > filteredAccounts.length && (
        <div className="text-center text-sm text-birhaus-muted-foreground">
          {language === 'es' ? (
            <>Mostrando {filteredAccounts.length} de {accounts.length} cuentas</>
          ) : (
            <>Showing {filteredAccounts.length} of {accounts.length} accounts</>
          )}
        </div>
      )}
    </div>
  )
}

export type { BirhausAccountSummaryProps }